export const logLevels = ['debug', 'info', 'warn', 'error'] as const;
export type LogLevel = (typeof logLevels)[number];

export const defaultLogLevelIcons = {
	debug: '🐛',
	info: 'ℹ️',
	warn: '⚠️',
	error: '🚨',
} as const;

export type LoggerConfig<TLoggerName extends string> = {
	enabledLoggers: Record<TLoggerName, boolean>;
	logLevelIcons?: Partial<Record<LogLevel, string>>;
	defaultAllowedLevels?: LogLevel[];
	timeFormat?: Intl.DateTimeFormatOptions;
};

// Define the Logger class type outside the factory
export interface ILogger<TLoggerName extends string> {
	readonly allowedLevels: LogLevel[];
	readonly scopes: string[];
	readonly name: TLoggerName;
	isAllowed(level: LogLevel): boolean;
	formatTimestamp(): string;
	log(level: LogLevel, ...args: unknown[]): void;
	debug(...args: unknown[]): void;
	info(...args: unknown[]): void;
	warn(...args: unknown[]): void;
	error(...args: unknown[]): void;
	extend(...scopes: string[]): ILogger<TLoggerName>;
}

/**
 * Creates a logger factory with the specified configuration
 * @param config Logger configuration including enabled loggers and optional customizations
 * @returns A factory function to create new loggers
 */
export function createLoggerFactory<TLoggerName extends string>(config: LoggerConfig<TLoggerName>) {
	const {
		enabledLoggers,
		logLevelIcons = defaultLogLevelIcons,
		defaultAllowedLevels = ['warn', 'error'],
		timeFormat = {
			hour12: false,
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric',
		},
	} = config;

	/**
	 * Logger class to manage application logging
	 */
	class Logger implements ILogger<TLoggerName> {
		public readonly allowedLevels: LogLevel[];
		public readonly scopes: string[];
		public readonly name: TLoggerName;

		constructor(name: TLoggerName, scopes: string[] = [], allowed?: LogLevel[]) {
			this.allowedLevels = allowed ?? defaultAllowedLevels;
			this.scopes = scopes;
			this.name = name;
		}

		public isAllowed(level: LogLevel): boolean {
			const isLevelAllowed = this.allowedLevels.includes(level);
			const isLoggerEnabled = enabledLoggers[this.name] ?? false;
			return isLevelAllowed && isLoggerEnabled;
		}

		public formatTimestamp(): string {
			return new Date().toLocaleTimeString('en-US', timeFormat);
		}

		public log(level: LogLevel, ...args: unknown[]): void {
			if (!this.isAllowed(level)) return;

			const timestamp = this.formatTimestamp();
			const lineHead = [`[${timestamp}]`, logLevelIcons[level], `[${this.name}]`];

			if (this.scopes.length > 0) {
				lineHead.unshift(`[${this.scopes.join('] [')}]`);
			}

			// eslint-disable-next-line no-console
			console[level](...lineHead, ...args);
		}

		public debug(...args: unknown[]): void {
			this.log('debug', ...args);
		}

		public info(...args: unknown[]): void {
			this.log('info', ...args);
		}

		public warn(...args: unknown[]): void {
			this.log('warn', ...args);
		}

		public error(...args: unknown[]): void {
			this.log('error', ...args);
		}

		/**
		 * Creates a new logger instance with additional scopes
		 * @param scopes Additional scopes to add to the logger
		 * @returns A new Logger instance with combined scopes
		 */
		public extend(...scopes: string[]): ILogger<TLoggerName> {
			return new Logger(this.name, [...this.scopes, ...scopes], this.allowedLevels);
		}
	}

	/**
	 * Creates a new logger instance
	 * @param name Logger name (must be one of the enabled loggers)
	 * @param scopes Optional array of scopes
	 * @param allowedLevels Optional array of allowed log levels
	 * @returns A new Logger instance
	 */
	return function createLogger(name: TLoggerName, scopes: string[] = [], allowedLevels?: LogLevel[]): ILogger<TLoggerName> {
		return new Logger(name, scopes, allowedLevels);
	};
}
